/*
 * @Author: linguang linguang@nextmar.com
 * @Date: 2025-04-11 17:40:31
 * @LastEditors: Please set LastEditors
 * @LastEditTime: 2025-09-08 17:44:05
 * @FilePath: /src/components/Form/Radio/index.tsx
 * @Description: 因为Radio组件和MUI的选中态的主题色是相反的，所以通过主题定义无法实现，
 */
import {
  Box,
  FormControlLabel,
  FormHelperText,
  Radio as MuiRadio,
  RadioProps as MuiRadioProps,
  SxProps,
  Theme,
} from '@mui/material';
import { SuccessFilled } from '@zeelool/icon';
import React from 'react';
import { Control, Controller, RegisterOptions, useForm } from 'react-hook-form';

interface ZLRadioProps extends MuiRadioProps {
  /**
   * 是否选中
   */
  checked?: boolean;
  /**
   * 选择框大小
   */
  size?: 'small' | 'medium';
  /**
   * 点击事件
   */
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /**
   * react-hook-form 字段名
   */
  name: string;
  /**
   * react-hook-form 控制器
   */
  control: Control<any>;
  /**
   * react-hook-form 验证规则
   */
  rules?: RegisterOptions;
  /**
   * Radio的值，用于react-hook-form
   */
  value?: any;
  /**
   * 标签
   */
  label?: React.ReactNode;
  /**
   * 错误帮助文本
   */
  helperText?: string;
  /**
   * 是否禁用
   */
  disabled?: boolean;
  /**
   * 自定义样式
   */
  sx?: SxProps<Theme>;

  /**
   * 标签位置
   */
  labelPlacement?: 'start' | 'end' | 'top' | 'bottom';

  /**
   * 选中状态的图标
   */
  checkedIcon?: React.ReactNode;

  /**
   * 图标大小
   */
  iconSize?: number;
}

export default function Radio({
  checked,
  size = 'medium',
  onChange,
  name = 'radio',
  control,
  rules,
  value,
  label,
  helperText,
  disabled = false,
  sx,
  labelPlacement = 'end',
  checkedIcon,
  iconSize = 18,
  ...restProps
}: ZLRadioProps) {
  // 如果未提供control，则创建一个默认的form控制器
  const defaultMethods = useForm();
  const resolvedControl = control || defaultMethods.control;

  // 默认的选中图标
  const defaultCheckedIcon = (
    <SuccessFilled className="text-[#463AE8]" style={{ fontSize: iconSize }} />
  );
  const resolvedCheckedIcon = checkedIcon || defaultCheckedIcon;

  // 总是使用Controller
  return (
    <Controller
      name={name}
      control={resolvedControl}
      rules={rules}
      defaultValue={checked || value || false}
      render={({ field, fieldState: { error } }) => {
        // 优先级：props.checked > props.value 与 RHF 值 > RHF 布尔值
        const isChecked = checked ?? (value !== undefined ? field.value === value : !!field.value);

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const newValue = value !== undefined ? value : e.target.checked;
          field.onChange(newValue);
          if (onChange) {
            onChange(e);
          }
        };

        const radioElement = (
          <Box className={`zl-radio ${isChecked ? 'zl-radio-checked' : ''}`}>
            <MuiRadio
              className="pl-0"
              checked={isChecked}
              onChange={handleChange}
              size={size}
              disabled={disabled}
              value={value}
              sx={sx}
              checkedIcon={resolvedCheckedIcon}
              icon={
                disabled ? (
                  <div className="w-[20px] h-[20px] bg-[#e1e1e1] rounded-[50%]" />
                ) : (
                  <div className="w-[20px] h-[20px] rounded-[50%] border-[1.5px] border-[#A9A9A9]" />
                )
              }
              {...restProps}
            />
          </Box>
        );

        return (
          <>
            {label ? (
              <FormControlLabel
                control={radioElement}
                label={label}
                disabled={disabled}
                sx={sx}
                labelPlacement={labelPlacement}
              />
            ) : (
              radioElement
            )}
            {error && <FormHelperText error>{error.message}</FormHelperText>}
            {!error && helperText && <FormHelperText>{helperText}</FormHelperText>}
          </>
        );
      }}
    />
  );
}
